import React, { useState } from 'react';
import './SubscriptionPage.css';
import { SUBSCRIPTION_PLANS, PlanType } from '../config/subscriptionPlans';
import { subscribeUser } from '../services/subscriptionService';

interface SubscriptionPageProps {
  msisdn: string;
  phoneNumber: string;
  onSubscribeSuccess: () => void;
  onNotify: (message: string, type: 'success' | 'error') => void;
}

const SubscriptionPage: React.FC<SubscriptionPageProps> = ({
  msisdn,
  phoneNumber,
  onSubscribeSuccess,
  onNotify,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('monthly');
  const [couponCode, setCouponCode] = useState('');
  const [subscribingPlanId, setSubscribingPlanId] = useState<PlanType | null>(null);

  const selectedPlanData = SUBSCRIPTION_PLANS.find((plan) => plan.id === selectedPlan);

  const handlePlanSelect = (planId: PlanType) => {
    if (subscribingPlanId) {
      return;
    }
    setSelectedPlan(planId);
  };

  const applyCoupon = () => {
    if (couponCode.trim()) {
      onNotify('Coupon feature coming soon', 'error');
    }
  };

  const handleSubscribe = async (planId: PlanType) => {
    const plan = SUBSCRIPTION_PLANS.find((item) => item.id === planId);
    if (!plan || subscribingPlanId) {
      return;
    }

    setSelectedPlan(planId);
    setSubscribingPlanId(planId);

    try {
      const result = await subscribeUser(
        msisdn,
        plan.id,
        plan.subscriptionId,
        phoneNumber,
        plan.durationDays
      );

      if (result.success) {
        onNotify('You are subscribed', 'success');
        onSubscribeSuccess();
        return;
      }

      onNotify(result.errorMessage || 'There is an error please try again', 'error');
    } finally {
      setSubscribingPlanId(null);
    }
  };

  return (
    <div className="subscription-page">
      <div className="background-effects">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      <div className="subscription-container">
        <div className="subscription-header">
          <h1 className="main-title">Choose Your <span className="highlight">GlobalSport</span> Plan</h1>
          <p className="subtitle">Unlock unlimited video content and premium features</p>
          <p className="subtitle">Subscribing for {phoneNumber}</p>
        </div>

        <div className="plans-section">
          <div className="plans-grid">
            {SUBSCRIPTION_PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`plan-card ${plan.popular ? 'popular' : ''} ${selectedPlan === plan.id ? 'selected' : ''}`}
                onClick={() => handlePlanSelect(plan.id)}
              >
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                {plan.discount && <div className="discount-badge">{plan.discount}</div>}

                <div className="plan-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-duration">{plan.duration}</p>
                </div>

                <div className="plan-pricing">
                  <div className="price-container">
                    <span className="currency">SZL</span>
                    <span className="price">{plan.price}</span>
                    {plan.originalPrice && (
                      <span className="original-price">SZL{plan.originalPrice}</span>
                    )}
                  </div>
                </div>

                <div className="plan-features">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <span className="check-icon">✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  className={`select-plan-btn subscribe-plan-btn ${selectedPlan === plan.id ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubscribe(plan.id);
                  }}
                  disabled={subscribingPlanId !== null}
                >
                  {subscribingPlanId === plan.id ? (
                    <>
                      <span className="subscribe-btn-spinner" aria-hidden="true" />
                      <span>Subscribing...</span>
                    </>
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="coupon-section">
          <h3>Have a coupon code?</h3>
          <div className="coupon-input-group">
            <input
              type="text"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              className="coupon-input"
            />
            <button className="apply-coupon-btn" onClick={applyCoupon}>
              Apply
            </button>
          </div>
        </div>

        {selectedPlanData && (
          <div className="payment-section">
            <div className="selected-plan-summary">
              <h3>Selected Plan: {selectedPlanData.name}</h3>
              <div className="plan-details">
                <span>Duration: {selectedPlanData.duration}</span>
                <span>Price: SZL{selectedPlanData.price}</span>
              </div>
            </div>
          </div>
        )}

        <div className="terms-section">
          <p>
            By proceeding with the payment, you agree to our{' '}
            <a href="#" className="terms-link">Terms of Service</a> and{' '}
            <a href="#" className="terms-link">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
